import React, { useEffect, useState } from 'react';

import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';

import { ILoyaltyTransaction } from '@rbi-ctg/menu';
import { CardModal } from 'components/card-modal';
import { toaster } from 'components/toaster/toaster';
import {
  TSM_GET_USER_ORDER_STATUSES,
  useTopServiceModeDetails,
} from 'components/top-service-mode/hooks/use-top-service-details';
import { ServiceMode } from 'generated/graphql-gateway';
import { useOrderStatus } from 'hooks/order-status';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import ConfirmationError from 'pages/cart/cart-error';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useLoyaltyContext } from 'state/loyalty';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { OrderStatus } from 'state/order/types';
import { routes } from 'utils/routing';
import { isDelivery } from 'utils/service-mode';
import { stringToBool } from 'utils/string-To-Bool';

import { OrderConfirmationPageContent } from './order-confirmation-page-content';

export const getRewardsAndPointsMessage = ({
  loyaltyTransaction,
  loyaltyEnabled,
}: {
  loyaltyTransaction: ILoyaltyTransaction | undefined;
  loyaltyEnabled: boolean;
}): string => {
  if (!loyaltyEnabled || !loyaltyTransaction) {
    return '';
  }

  const successMessages: string[] = [];
  const { rewardsUsed, pointsEarned, bonusPointsEarned } = loyaltyTransaction;

  if (loyaltyTransaction.rewardsUsed) {
    successMessages.push(
      t('redeemedRewards', {
        redeemedRewards: rewardsUsed,
      })
    );
  }

  if (pointsEarned || bonusPointsEarned) {
    successMessages.push(
      t('pointsEarned', {
        pointsEarned: pointsEarned + bonusPointsEarned,
      })
    );
  }

  return successMessages.join('\n');
};

export const OrderConfirmationPage = () => {
  useRumPageView('order-confirmation', 'Order Confirmation');

  const params = useLocalSearchParams<{
    rbiOrderId: string;
    fromActivity?: string;
    dismissOnly?: string;
  }>();

  const rbiOrderId = params.rbiOrderId;
  const dismissOnly = stringToBool(params.dismissOnly);
  const fromLiveActivity = stringToBool(params.fromActivity);

  const router = useRouter();
  const [skipOrderStatusPolling, setSkipOrderStatusPolling] = useState(false);
  const { serverOrder, isLoading, orderStatus } = useOrderStatus({
    rbiOrderId,
    skip: skipOrderStatusPolling,
  });

  useEffect(() => {
    if (fromLiveActivity) {
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.LIVE_ACTIVITY_DEEPLINK,
          name: 'Order Details',
        },
      });
    }
  }, [fromLiveActivity]);

  const { refetchUserOrder } = useTopServiceModeDetails();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const serviceMode: ServiceMode | null = serverOrder ? serverOrder.cart.serviceMode : null;

  const { showToast } = useLocalSearchParams<{
    showToast?: string;
  }>();
  // Both routers handle QS params parsing differently
  const showToastBool = stringToBool(showToast);

  const message =
    showToastBool && serverOrder?.loyaltyTransaction
      ? getRewardsAndPointsMessage({
          loyaltyTransaction: serverOrder.loyaltyTransaction,
          loyaltyEnabled,
        })
      : '';

  useEffect(() => {
    if (message) {
      toaster.positive(message);
    }
  }, [message]);

  useEffect(() => {
    if (orderStatus && (TSM_GET_USER_ORDER_STATUSES as string[]).includes(orderStatus)) {
      // Refetch loyaltyUser to obtain new point balance
      if (loyaltyEnabled) {
        refetchLoyaltyUser();
      }
      // Ensure BSM has the latest user order
      refetchUserOrder();
    }
  }, [loyaltyEnabled, orderStatus, refetchLoyaltyUser, refetchUserOrder]);

  useEffect(() => {
    if (!isDelivery(serviceMode)) {
      setSkipOrderStatusPolling(false);
    }
  }, [serviceMode]);

  if (!isLoading && !serverOrder) {
    return <Redirect href={routes.notFound404} />;
  }

  const insertFailed =
    orderStatus && !![OrderStatus.PAYMENT_ERROR, OrderStatus.INSERT_ERROR].includes(orderStatus);

  if (insertFailed) {
    if (orderStatus === OrderStatus.PAYMENT_ERROR) {
      return (
        <ConfirmationError
          title={t('paymentDeclinedHeading')}
          body={t('paymentDeclined')}
          disclaimer={t('paymentDeclinedDisclaimer')}
          onDismiss={() => router.replace(routes.menu)}
          additionalLoggingContext={{ orderStatus, insertFailed }}
        />
      );
    }
    return <ConfirmationError additionalLoggingContext={{ orderStatus, insertFailed }} />;
  }

  return (
    <CardModal
      invertHeaderColor
      testID="order-confirmation"
      headerText={t('orderDetailsHeading')}
      helmetTitle={t('orderConfirmation')}
      onDismiss={() => (dismissOnly ? router.dismiss() : router.replace(routes.base))}
      scrollView
      standardPadding
      modalContainerStyle={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
    >
      <OrderConfirmationPageContent loading={isLoading} serverOrder={serverOrder} />
    </CardModal>
  );
};
